// HeroSearchPill — unified search bar for /home-beta hero.
//
// Mirrors the search pill on the production homepage (ConsumerHomepage.tsx):
//   [ Scenario ▾ | search input ]
//   [ ZIP code  |             SEARCH ]
// + autocomplete dropdown
//
// Categories: Scenario, Procedure, CPT, HCPCS, REV, MS-DRG.
// Scenarios are filtered in-memory (already loaded for the catalog row);
// other categories hit Supabase pricing-average tables.
//
// Theme tokens come in via props so the component matches whichever hero it's
// dropped into — same patriotic palette as HomeBeta (navy / gold / red).
//
// Why a separate component from ConsumerHomepage's inline version: keeping the
// production homepage code path untouched while /home-beta iterates. Once
// /home-beta is the canonical homepage, the two should be consolidated.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { loadStaticData } from "@/lib/offlineData";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

// Categories — same shape and labels as the production homepage so users
// switching between /home-beta and / don't see a different vocabulary.
// `labelKey` + `placeholderKey` reference `hero.searchCategories.*` in
// homepage.json so the existing translations cover both surfaces.
const SEARCH_CATEGORIES = [
  { value: "scenario",  labelKey: "scenario",  placeholderKey: "scenarioPlaceholder" },
  { value: "procedure", labelKey: "procedure", placeholderKey: "procedurePlaceholder" },
  { value: "cpt",       labelKey: "cpt",       placeholderKey: "cptPlaceholder" },
  { value: "hcpcs",     labelKey: "hcpcs",     placeholderKey: "hcpcsPlaceholder" },
  { value: "rev",       labelKey: "rev",       placeholderKey: "revPlaceholder" },
  { value: "ms-drg",    labelKey: "msDrg",     placeholderKey: "msDrgPlaceholder" },
] as const;

type SearchCategoryValue = typeof SEARCH_CATEGORIES[number]["value"];

interface AutocompleteItem {
  id: string;
  label: string;
  sublabel?: string;
  price?: string;
  category: string;
  icon?: string;
}

// Currency formatter for autocomplete price labels.
function fmtPrice(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Math.round(Number(v));
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

interface ThemeShape {
  text: string;
  textMuted: string;
  red: string;
  gold: string;
  blue: string;
  navy: string;
  cardBg: string;
  cardBorder: string;
  divider: string;
  bg: string;
}

interface Props {
  T: ThemeShape;
}

export function HeroSearchPill({ T }: Props) {
  const { t } = useTranslation(['homepage', 'common']);
  const navigate = useNavigate();
  // Translation helpers for the search category dropdown — both labels +
  // placeholders live under `hero.searchCategories.*` so HomeBeta + the
  // legacy ConsumerHomepage share a single translation source.
  const catLabel = (key: string) => t(`homepage:hero.searchCategories.${key}`);
  const catPlaceholder = (key: string) => t(`homepage:hero.searchCategories.${key}`);

  // ─── Scenarios index (shared cache with the catalog row) ───────────────────
  const { data: scenarios = [] } = useQuery({
    queryKey: ["scenarios-index-for-search"],
    queryFn: async (): Promise<Array<{ id: string; name: string; description: string; tags: string[]; calculated_avg_cost?: number; severityLevel?: number; scenarioFamilyId?: string | null }>> => {
      try {
        const staticData = await loadStaticData<{ generated_at: string; data: any[] }>("scenarios_index.json");
        if (!staticData || !Array.isArray(staticData.data)) return [];
        return staticData.data
          .filter((s: any) => !s.hidden)
          .map((s: any) => ({
            id: s.id,
            name: s.name || "",
            description: s.description || "",
            tags: Array.isArray(s.tags) ? s.tags : [],
            calculated_avg_cost: s.estimatedCost ?? s.calculated_avg_cost,
            // severity + family drive base-first ranking & per-family dedup so a
            // search surfaces the family root (e.g. "Uncomplicated Vaginal
            // Delivery") rather than an arbitrary variant ("…Unplanned ER Arrival").
            severityLevel: s.severityLevel ?? 0,
            scenarioFamilyId: s.scenarioFamilyId ?? null,
          }));
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // ─── Input state ───────────────────────────────────────────────────────────
  const [searchCategory, setSearchCategory] = useState<SearchCategoryValue>("scenario");
  const [procedure, setProcedure] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ─── Autocomplete state ────────────────────────────────────────────────────
  const [acResults, setAcResults] = useState<AutocompleteItem[]>([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [acSelected, setAcSelected] = useState(-1);

  // ─── Refs for click-outside dismissal + input focus ───────────────────────
  const formRef = useRef<HTMLFormElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const acTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const selectedCategory = useMemo(
    () => SEARCH_CATEGORIES.find((c) => c.value === searchCategory) || SEARCH_CATEGORIES[0],
    [searchCategory]
  );

  // Click-outside dismissal for both the category dropdown and the autocomplete.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) setAcOpen(false);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced autocomplete fetcher. Scenarios are filtered in-memory; all other
  // categories hit Supabase's *_pricing_averages views.
  const fetchAutocomplete = useCallback(async (query: string, cat: SearchCategoryValue) => {
    const q = query.trim();
    if (q.length < 2) {
      setAcResults((prev) => (prev.length === 0 ? prev : []));
      setAcOpen(false);
      return;
    }
    setAcLoading(true);
    let items: AutocompleteItem[] = [];
    try {
      if (cat === "scenario") {
        const lq = q.toLowerCase();
        // Relevance score: name-prefix > name-contains > tag/desc match. Family
        // bases (severity 0) get a strong boost over variants so "preg" surfaces
        // "Uncomplicated Vaginal Delivery", not "…— Unplanned ER Arrival".
        const scored = scenarios
          .map((s) => {
            const name = s.name.toLowerCase();
            const nameHit = name.includes(lq);
            const tagHit = s.tags.some((t) => t.toLowerCase().includes(lq));
            const descHit = s.description.toLowerCase().includes(lq);
            if (!nameHit && !tagHit && !descHit) return null;
            let score = 0;
            if (name.startsWith(lq)) score += 100;
            else if (nameHit) score += 60;
            else if (tagHit) score += 25;
            else if (descHit) score += 10;
            if ((s.severityLevel ?? 0) === 0) score += 40; // family base / no-complications
            else score -= 10 * Math.min(s.severityLevel ?? 0, 4); // demote variants
            return { s, score };
          })
          .filter(Boolean) as Array<{ s: typeof scenarios[number]; score: number }>;
        // Tie-break by name length asc: the shorter name is the more canonical /
        // general scenario (e.g. "Uncomplicated Pregnancy & Vaginal Delivery" beats
        // the niche "Substance Use Disorder MAT — with Pregnancy and Neonatal …").
        scored.sort((a, b) => (b.score - a.score) || (a.s.name.length - b.s.name.length));
        // Dedup per family — keep only the highest-scoring member (the base) of
        // each family, so a family contributes one result, not every variant.
        const seenFamily = new Set<string>();
        const deduped: typeof scored = [];
        for (const entry of scored) {
          const fam = entry.s.scenarioFamilyId;
          if (fam) {
            if (seenFamily.has(fam)) continue;
            seenFamily.add(fam);
          }
          deduped.push(entry);
        }
        items = deduped
          .slice(0, 8)
          .map(({ s }) => ({
            id: s.id,
            label: s.name,
            sublabel: s.description.length > 80 ? s.description.slice(0, 80) + "…" : s.description,
            price: fmtPrice(s.calculated_avg_cost) ? `~${fmtPrice(s.calculated_avg_cost)} avg` : undefined,
            category: "Scenario",
            icon: "★",
          }));
      } else if (cat === "procedure") {
        const { data } = await supabase
          .from("procedures")
          .select("code, code_type, name, llm_quick_name")
          .or(`name.ilike.%${q}%,llm_quick_name.ilike.%${q}%,code.ilike.%${q}%`)
          .limit(10);
        items = (data || []).map((r: any) => ({
          id: r.code,
          label: r.llm_quick_name || r.name || r.code,
          sublabel: r.code_type ? `${r.code_type} ${r.code}` : r.code,
          category: "Procedure",
          icon: "⚕",
        }));
      } else if (cat === "cpt") {
        const { data } = await supabase
          .from("cpt_pricing_averages")
          .select("cpt_code, procedure_description, avg_gross_charge, total_hospitals")
          .eq("setting_type", "all_settings")
          .or(`cpt_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map((r: any) => ({
          id: r.cpt_code,
          label: r.cpt_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "…" : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `CPT · ${r.total_hospitals || 0} hospitals`,
          icon: "🏥",
        }));
      } else if (cat === "hcpcs") {
        const { data } = await supabase
          .from("hcpcs_pricing_averages")
          .select("hcpcs_code, procedure_description, avg_gross_charge, total_hospitals")
          .eq("setting_type", "all_settings")
          .or(`hcpcs_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map((r: any) => ({
          id: r.hcpcs_code,
          label: r.hcpcs_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "…" : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `HCPCS · ${r.total_hospitals || 0} hospitals`,
          icon: "💊",
        }));
      } else if (cat === "rev") {
        const { data } = await supabase
          .from("revenue_pricing_averages")
          .select("revenue_code, revenue_description, avg_charge, total_hospitals")
          .or(`revenue_code.ilike.%${q}%,revenue_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map((r: any) => ({
          id: r.revenue_code,
          label: r.revenue_code,
          sublabel: r.revenue_description?.length > 70 ? r.revenue_description.slice(0, 70) + "…" : r.revenue_description,
          price: fmtPrice(r.avg_charge) ? `${fmtPrice(r.avg_charge)} avg` : undefined,
          category: `REV · ${r.total_hospitals || 0} hospitals`,
          icon: "🏥",
        }));
      } else if (cat === "ms-drg") {
        const exactCode = `MS-${q}`;
        const { data } = await supabase
          .from("drg_pricing_averages")
          .select("drg_code, procedure_description, avg_gross_charge, total_hospitals")
          .or(`drg_code.ilike.%${exactCode}%,drg_code.ilike.%${q}%,procedure_description.ilike.%${q}%`)
          .order("total_hospitals", { ascending: false })
          .limit(10);
        items = (data || []).map((r: any) => ({
          id: r.drg_code,
          label: r.drg_code?.replace("MS-", "") || r.drg_code,
          sublabel: r.procedure_description?.length > 70 ? r.procedure_description.slice(0, 70) + "…" : r.procedure_description,
          price: fmtPrice(r.avg_gross_charge) ? `${fmtPrice(r.avg_gross_charge)} avg` : undefined,
          category: `MS-DRG · ${r.total_hospitals || 0} hospitals`,
          icon: "🏥",
        }));
      }
    } catch (err) {
      console.warn("[HeroSearchPill] autocomplete error:", err);
    }
    setAcResults(items);
    setAcOpen(items.length > 0);
    setAcLoading(false);
    setAcSelected(-1);
  }, [scenarios]);

  // Debounced trigger on input / category change.
  useEffect(() => {
    if (acTimerRef.current) clearTimeout(acTimerRef.current);
    acTimerRef.current = setTimeout(
      () => fetchAutocomplete(procedure, searchCategory),
      searchCategory === "scenario" ? 100 : 250 // in-memory: snappier
    );
    return () => { if (acTimerRef.current) clearTimeout(acTimerRef.current); };
  }, [procedure, searchCategory, fetchAutocomplete]);

  // Click an autocomplete row: scenarios go to detail page, everything else to /search.
  const handleAcSelect = (item: AutocompleteItem) => {
    setProcedure(item.label);
    setAcOpen(false);
    if (item.category === "Scenario" && item.id) {
      const slug = item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      navigate(`/scenarios/${item.id}/${slug}`);
      return;
    }
    const params = new URLSearchParams();
    params.set("q", item.label);
    params.set("searchType", searchCategory);
    if (zipCode) params.set("zip", zipCode);
    navigate(`/search?${params.toString()}`);
  };

  // Keyboard navigation: ↑ / ↓ / Enter / Esc.
  const handleAcKeyDown = (e: React.KeyboardEvent) => {
    if (!acOpen || acResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAcSelected((prev) => Math.min(prev + 1, acResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAcSelected((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && acSelected >= 0) {
      e.preventDefault();
      handleAcSelect(acResults[acSelected]);
    } else if (e.key === "Escape") {
      setAcOpen(false);
    }
  };

  // Submit handler (button click or Enter without a highlighted ac row).
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAcOpen(false);
    const params = new URLSearchParams();
    if (procedure) params.set("q", procedure);
    params.set("searchType", searchCategory);
    if (zipCode) params.set("zip", zipCode);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSearch}
      className="max-w-3xl mx-auto"
      style={{ position: "relative" }}
    >
      <div
        className="flex flex-col items-stretch"
        style={{
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 12,
          backgroundColor: T.cardBg,
          boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
          overflow: "visible",
          position: "relative",
        }}
      >
        {/* Top row: category dropdown + query input */}
        <div className="flex items-stretch flex-1">
          {/* Category dropdown */}
          <div ref={dropdownRef} className="relative flex-shrink-0" style={{ zIndex: 20 }}>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-4 h-full transition-colors"
              style={{
                fontFamily: sans,
                fontSize: 14,
                fontWeight: 500,
                // T.blue (theme-aware: lighter #5B9BD5 in dark mode, deep
                // var(--civic-navy) in light) — matches what the production homepage
                // uses for this exact dropdown label. Visible against the
                // navy card bg in dark mode AND keeps the brand-blue accent.
                color: T.blue,
                backgroundColor: dropdownOpen ? "rgba(var(--civic-navy-rgb),0.06)" : "transparent",
                borderRight: `1px solid ${T.cardBorder}`,
                borderRadius: "12px 0 0 12px",
                whiteSpace: "nowrap",
              }}
            >
              <span>{catLabel(selectedCategory.labelKey)}</span>
              <ChevronDown
                className="w-4 h-4 transition-transform"
                style={{ color: T.textMuted, transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </button>
            {dropdownOpen && (
              <div
                className="absolute top-full left-0 mt-1 py-1"
                style={{
                  backgroundColor: T.cardBg,
                  border: `1px solid ${T.cardBorder}`,
                  borderRadius: 10,
                  boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
                  minWidth: 180,
                  zIndex: 50,
                }}
              >
                {SEARCH_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      setSearchCategory(cat.value);
                      setDropdownOpen(false);
                      searchInputRef.current?.focus();
                    }}
                    className="w-full text-left px-4 py-2.5 transition-colors flex items-center gap-2"
                    style={{
                      fontFamily: sans,
                      fontSize: 14,
                      color: searchCategory === cat.value ? T.red : T.text,
                      backgroundColor: searchCategory === cat.value ? "rgba(var(--civic-red-rgb),0.06)" : "transparent",
                      fontWeight: searchCategory === cat.value ? 600 : 400,
                    }}
                  >
                    {searchCategory === cat.value && (
                      <span style={{ color: T.red, fontSize: 10 }}>●</span>
                    )}
                    {catLabel(cat.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Query input */}
          <div className="flex items-center gap-3 flex-1 px-4 sm:px-5 min-w-0">
            <Search className="w-5 h-5 flex-shrink-0" style={{ color: T.textMuted }} />
            <input
              ref={searchInputRef}
              type="text"
              value={procedure}
              onChange={(e) => setProcedure(e.target.value)}
              onKeyDown={handleAcKeyDown}
              onFocus={() => { if (acResults.length > 0) setAcOpen(true); }}
              placeholder={catPlaceholder(selectedCategory.placeholderKey)}
              // home-beta-pill-input drives the placeholder color via
              // src/index.css using the [data-color-scheme="dark"] attribute
              // selector. Tailwind's dark: variant relies on a .dark class
              // that this app doesn't set, so the previous dark:placeholder
              // utility silently failed.
              className="home-beta-pill-input w-full py-4 outline-none bg-transparent"
              style={{ fontFamily: sans, fontSize: 16, color: T.text }}
              autoComplete="off"
            />
          </div>
        </div>

        {/* Bottom row: ZIP + submit */}
        <div className="flex items-stretch" style={{ borderTop: `1px solid ${T.cardBorder}` }}>
          <div className="flex items-center gap-2 px-5 flex-1">
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder={t('homepage:hero.zipPlaceholder')}
              maxLength={5}
              inputMode="numeric"
              className="home-beta-pill-input w-full py-3 sm:py-4 outline-none bg-transparent text-center sm:text-left"
              style={{ fontFamily: sans, fontSize: 16, color: T.text }}
            />
          </div>
          <button
            type="submit"
            className="px-8 py-3 sm:py-4 font-medium transition-opacity hover:opacity-90"
            style={{
              backgroundColor: T.red,
              color: "#fff",
              fontFamily: sans,
              fontSize: 15,
              letterSpacing: "0.5px",
              borderRadius: "0 0 12px 0",
            }}
          >
            {t('common:actions.search')}
          </button>
        </div>
      </div>

      {/* ─── Autocomplete dropdown ───────────────────────────────────────── */}
      {acOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 6,
            backgroundColor: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 12,
            boxShadow: "0 12px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            zIndex: 100,
            overflow: "hidden",
            maxHeight: 420,
            overflowY: "auto",
          }}
        >
          {/* Category indicator */}
          <div
            style={{
              padding: "10px 16px 8px",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: sans,
              // Same theme-aware brand blue as the dropdown label above —
              // mirrors the production homepage's category indicator color.
              color: T.blue,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: T.red, display: "inline-block" }} />
            {catLabel(selectedCategory.labelKey)} {t('homepage:hero.results')}
            {acLoading && (
              <span style={{ marginLeft: "auto", fontSize: 10, color: T.textMuted, fontWeight: 400 }}>
                {t('common:searching')}
              </span>
            )}
          </div>

          {/* Result rows */}
          {acResults.map((item, idx) => (
            <button
              key={item.id + idx}
              type="button"
              onClick={() => handleAcSelect(item)}
              onMouseEnter={() => setAcSelected(idx)}
              className="w-full text-left transition-colors"
              style={{
                padding: "12px 16px",
                fontFamily: sans,
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                backgroundColor: acSelected === idx ? "rgba(var(--civic-navy-rgb),0.06)" : "transparent",
                borderBottom: idx < acResults.length - 1 ? `1px solid ${T.divider}` : "none",
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1, marginTop: 2, minWidth: 24, textAlign: "center" }}>
                {item.icon || "•"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: T.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </span>
                  {item.price && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: T.gold,
                        fontFamily: "'JetBrains Mono', monospace",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {item.price}
                    </span>
                  )}
                </div>
                {item.sublabel && (
                  <div
                    style={{
                      fontSize: 13,
                      color: T.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.sublabel}
                  </div>
                )}
                <div style={{ fontSize: 11, color: T.blue, marginTop: 3, fontWeight: 500, opacity: 0.6 }}>
                  {item.category}
                </div>
              </div>
              <ArrowRight
                className="flex-shrink-0"
                style={{ width: 16, height: 16, color: acSelected === idx ? T.red : T.textMuted, marginTop: 4 }}
              />
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
